'use client';

import { faCartShopping } from '@fortawesome/pro-regular-svg-icons';
import FormattedPrice from '@/components/common/formatted-price/formatted-price';
import TooltipProductCard from '@/components/common/tooltip-product-card/tooltip-product-card';
import { Badge, Button, Icon, IconButton, Tooltip } from '@/components/ui';
import useHeaderContent from '@/hooks/use-header-content';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import useNavigate from '@/hooks/use-navigate';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { cn } from '@/utils';
import { getShoppingCartProductsNum } from '@/utils/get-product-items-number';
import type { HeaderToolbarVariant } from './types';

export interface MyCartButtonProps {
  variant?: HeaderToolbarVariant;
  /** Smaller icon-only control for the mobile menu drawer footer. */
  compact?: boolean;
}

const MyCartButtonPreview = ({ variant = 'primary', compact = false }: MyCartButtonProps) => {
  const resolveThemeColor = useResolveThemeColor();
  const headerContent = useHeaderContent();
  const isSecondary = variant === 'secondary';
  const desktopBtnClass = isSecondary
    ? 'mr-4 hidden h-auto px-0.5 pt-4 pb-3 @xl:block'
    : 'hidden h-auto px-0.5 pt-4 pb-3 @lg:block';
  const mobileBtnClass = isSecondary
    ? 'flex h-auto p-1.5 @xl:hidden'
    : 'flex h-auto p-1.5 @lg:hidden';
  const toolbarTextResolved = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const iconColorStyle = { color: toolbarTextResolved };

  if (compact) {
    return (
      <IconButton
        aria-label="Cart"
        variant="light"
        className="h-auto min-h-0 overflow-visible p-1"
        size="sm"
        isDisabled
        style={iconColorStyle}
      >
        <Badge variant="solid" content={0} size="sm">
          <Icon icon={faCartShopping} size="sm" style={iconColorStyle} />
        </Badge>
      </IconButton>
    );
  }

  return (
    <>
      <Button
        aria-label="Cart"
        variant="light"
        className={cn(desktopBtnClass, 'overflow-visible')}
        size="sm"
        isDisabled
        style={iconColorStyle}
      >
        <Badge content={0} className="absolute -top-1 -right-1">
          <div className="flex flex-col gap-2">
            <Icon icon={faCartShopping} size="xl" style={iconColorStyle} />
          </div>
        </Badge>
      </Button>
      <IconButton
        aria-label="Cart"
        variant="light"
        className={cn(mobileBtnClass, 'overflow-visible')}
        size="lg"
        isDisabled
        style={iconColorStyle}
      >
        <Badge variant="solid" content={0}>
          <Icon icon={faCartShopping} size="lg" style={iconColorStyle} />
        </Badge>
      </IconButton>
    </>
  );
};

const MyCartButtonLive = ({ variant = 'primary', compact = false }: MyCartButtonProps) => {
  const resolveThemeColor = useResolveThemeColor();
  const { t } = useTranslation();
  const { myCart, isLoadingMyCart } = useMyCart();
  const navigate = useNavigate();
  const headerContent = useHeaderContent();

  const productsNum = getShoppingCartProductsNum(myCart?.products);

  const isSecondary = variant === 'secondary';
  const tooltipDesktopClass = isSecondary
    ? 'hidden overflow-hidden @xl:block'
    : 'hidden overflow-hidden @lg:block';
  const desktopBtnClass = isSecondary
    ? 'mr-4 hidden h-auto px-0.5 pt-4 pb-3 @xl:block'
    : 'hidden h-auto px-0.5 pt-4 pb-3 @lg:block';
  const mobileBtnClass = isSecondary
    ? 'flex h-auto p-1.5 @xl:hidden'
    : 'flex h-auto p-1.5 @lg:hidden';

  const toolbarTextResolved = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const iconColorStyle = { color: toolbarTextResolved };

  if (compact) {
    return (
      <IconButton
        aria-label="Cart"
        onClick={() => navigate('/my-cart')}
        variant="light"
        className="h-auto min-h-0 overflow-visible p-1"
        size="sm"
        isDisabled={false}
        style={iconColorStyle}
      >
        <Badge variant="solid" content={productsNum} size="sm" isInvisible={isLoadingMyCart}>
          <Icon icon={faCartShopping} size="sm" style={iconColorStyle} />
        </Badge>
      </IconButton>
    );
  }

  return (
    <>
      <Tooltip
        className={tooltipDesktopClass}
        classNames={{ content: 'p-2' }}
        shadow="lg"
        showArrow
        delay={300}
        content={
          <div className="flex max-h-96 w-[400px] flex-col overflow-hidden">
            <div className="flex scrollbar-thin flex-col overflow-y-auto">
              {myCart?.products.map(prod => (
                <TooltipProductCard key={prod.id} type="cart" product={prod} />
              ))}
            </div>
            <div className="border-t border-gray-200 p-4">
              {myCart && myCart.products.length > 0 && (
                <div className="mb-4 flex w-full items-center justify-between">
                  <span className="text-lg font-medium">{t?.common.layout.total}:</span>
                  <FormattedPrice price={myCart?.totalAmount} showVatSuffix isVerticalDirection />
                </div>
              )}
              <Button
                onPress={() => navigate('/my-cart')}
                className="block w-full text-white"
                style={{ backgroundColor: resolveThemeColor('primary') }}
              >
                {t?.common.layout.seeMyCart}
              </Button>
            </div>
          </div>
        }
      >
        <Button
          aria-label="Cart"
          onPress={() => navigate('/my-cart')}
          variant="light"
          className={cn(desktopBtnClass, 'overflow-visible')}
          size="sm"
          style={iconColorStyle}
        >
          <Badge
            content={productsNum}
            className="absolute -top-1 -right-1"
            isInvisible={isLoadingMyCart}
          >
            <div className="flex flex-col gap-2">
              <Icon icon={faCartShopping} size="xl" style={iconColorStyle} />
            </div>
          </Badge>
        </Button>
      </Tooltip>
      <IconButton
        aria-label="Cart"
        onClick={() => navigate('/my-cart')}
        variant="light"
        className={cn(mobileBtnClass, 'overflow-visible')}
        size="lg"
        isDisabled={false}
        style={iconColorStyle}
      >
        <Badge variant="solid" content={productsNum} isInvisible={isLoadingMyCart}>
          <Icon icon={faCartShopping} size="lg" style={iconColorStyle} />
        </Badge>
      </IconButton>
    </>
  );
};

const MyCartButton = (props: MyCartButtonProps) => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  if (isStudioPreview) {
    return <MyCartButtonPreview {...props} />;
  }
  return <MyCartButtonLive {...props} />;
};

export default MyCartButton;
